import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styles from './Config.module.css';

interface ConfigCard {
  title: string;
  description: string;
  to: string;
  state: string;
  disabled?: boolean;
}

interface ConfigSection {
  label: string;
  cards: ConfigCard[];
}

const SECTIONS: ConfigSection[] = [
  {
    label: 'Operação',
    cards: [
      {
        title: 'Geral',
        description:
          'Aparência, persona default, timezone, versão e estado do sistema.',
        to: '/config',
        state: 'pós-MVP',
        disabled: true,
      },
      {
        title: 'Usuários',
        description:
          'Equipe com acesso ao sistema. Criar, desativar e repor senha. Login por e-mail, troca no 1º acesso.',
        to: '/config/usuarios',
        state: 'MVP',
      },
      {
        title: 'Marca',
        description:
          'White-label em runtime: nome, logo, cor e tagline. Aplica na hora, sem rebuild. Onboarding de cliente novo sem cirurgia.',
        to: '/config/marca',
        state: 'S0',
      },
      {
        title: 'Sites WordPress',
        description:
          'Multi-site WP: URL, app password, default author, test connection.',
        to: '/config/sites',
        state: 'S23',
      },
      {
        title: 'Consumidores da API de dados',
        description:
          'Tokens de servidor amarrados a IP para quem consome /api/v1/dados (cotação, tempo, notícias, portal).',
        to: '/config/consumidores',
        state: 'API DE DADOS',
      },
      {
        title: 'AI Providers',
        description:
          'OpenAI, Anthropic, Gemini, Perplexity. Default model + budget mensal.',
        to: '/config/ai-providers',
        state: 'S23',
      },
      {
        title: 'Desempenho (Analytics)',
        description:
          'Conecte GA4 + Search Console por site. Liga o painel "Desempenho" no editor (tráfego, engajamento e busca por matéria).',
        to: '/config/analytics',
        state: 'B-11',
      },
      {
        title: 'Copiloto — parâmetros',
        description:
          'Os limiares que governam o Copiloto neste cliente: os seis portões, a régua de manchete e o tamanho do lead. O padrão da plataforma fica à vista ao lado de cada campo.',
        to: '/config/copiloto',
        state: 'WIRESEO',
      },
      {
        title: 'SEO — Endereço da matéria',
        description:
          'Regra global da URL enviada ao WordPress: limite de caracteres, palavras vazias removidas, preview ao vivo. Só para posts novos.',
        to: '/config/seo',
        state: 'B-49',
      },
      {
        title: 'Política de publicação',
        description:
          'Travas que valem para todos os caminhos de produção: piso de demanda, teto por assunto/dia, divulgação de automação, largura mínima da capa (Discover) e o calendário de feriados que define "dia útil" nas esteiras.',
        to: '/config/publicacao',
        state: 'motor',
      },
    ],
  },
  {
    label: 'Inteligência',
    cards: [
      {
        title: 'Estrategista & Copiloto IA',
        description:
          'Gerencie os parâmetros do motor prescritivo, modelo de IA, metas mensais de comissão de afiliados e sensibilidade das ordens de antecipação.',
        to: '/config/estrategista',
        state: 'Novo',
      },
      {
        title: 'Calendário Preditivo & Antecipação',
        description:
          'Gerencie as janelas de lead time, as praças cobertas, tags de afiliados e sensibilidade do motor estatístico de antecipação.',
        to: '/config/calendario',
        state: 'Novo',
      },
      {
        title: 'Editorias',
        description:
          'As editorias da esteira (feed → processamento → publicação): modo off/semi/auto, fontes, destino e disparo manual.',
        to: '/editorias',
        state: 'motor',
      },
      {
        title: 'Quentes · Esteira automática',
        description:
          'Esteira automática: rankeia tendências, enriquece (PAA + N fontes), escreve com IA e publica no WP. Editorias, tetos, corte de score e rascunho↔publicado.',
        to: '/config/quentes',
        state: 'MVP',
      },
      {
        title: 'Templates de Horóscopo',
        description:
          'O padrão diário de publicações (7 tipos): rótulo, regex que casa o título e estrutura-padrão do conteúdo.',
        to: '/config/horoscopo',
        state: 'MVP',
      },
      {
        title: 'Portal de esportes',
        description:
          'Site de esportes que se publica sozinho: tabelas, clubes, atletas, serviço de jogo e pós-jogo. Liga, ajusta, roda agora e mostra o que saiu.',
        to: '/config/portal-esportes',
        state: 'Novo',
      },
    ],
  },
  {
    label: 'Integrações',
    cards: [
      {
        title: 'TV (ION HUB)',
        description:
          'Integração com o MOS da TV: credenciais, saúde da conexão e teste de ingestão.',
        to: '/config/external-apis',
        state: 'Novo',
      },
      {
        title: 'APIs externas',
        description:
          'SERP API, SMTP, Twitter/X, FreeAstrology — credenciais e configs específicas.',
        to: '/config/external-apis',
        state: 'S23',
      },
      {
        title: 'Prompts',
        description:
          'Templates de prompt versionados por workflow (Axioma IV — engenharia travada).',
        to: '/config/prompts',
        state: 'S7/S8',
      },
      {
        title: 'Notificações',
        description:
          'Subscrições por persona + canais email / webhook / in-app.',
        to: '/config/notificacoes',
        state: 'S10',
      },
    ],
  },
  {
    label: 'Auditoria & Limites',
    cards: [
      {
        title: 'Audit log',
        description:
          'Registro append-only de todas as mutações (REST + MCP). Axioma V.',
        to: '/config/audit',
        state: 'S15',
      },
      {
        title: 'Custos & limites',
        description:
          'Gasto mensal por provider, budgets, alertas a 80% / 100%, histórico de 6 meses.',
        to: '/config/custos',
        state: 'S23',
      },
      {
        title: 'Credenciais',
        description:
          'Vista master de todas as API keys (Fernet-encrypted). Reveal + rotate audit-logged.',
        to: '/config/credenciais',
        state: 'S23',
      },
    ],
  },
];

export function Config() {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  return (
    <section className={styles.root} aria-label={t('config:title', 'Configurações')}>
      <header className={styles.header}>
        <h1 className={styles.title}>Configurações</h1>
        <p className={styles.subtitle}>
          Pontos únicos de configuração do sistema. Tudo o que muda
          comportamento ao vivo vive aqui — secrets ficam encriptados em DB.
        </p>
      </header>

      {SECTIONS.map((section) => (
        <div key={section.label} className={styles.section}>
          <h2 className={styles.sectionLabel}>{section.label}</h2>
          <div className={styles.grid}>
            {section.cards.map((card) =>
              card.disabled ? (
                <div
                  key={card.title}
                  className={`${styles.card} ${styles.cardDisabled}`}
                  aria-disabled
                >
                  <div className={styles.head}>
                    <span className={styles.cardTitle}>{card.title}</span>
                  </div>
                  <p className={styles.desc}>{card.description}</p>
                  <span className={styles.state}>{card.state}</span>
                </div>
              ) : (
                <Link
                  key={card.title}
                  to={card.to}
                  className={styles.card}
                >
                  <div className={styles.head}>
                    <span className={styles.cardTitle}>{card.title}</span>
                  </div>
                  <p className={styles.desc}>{card.description}</p>
                  <span className={styles.state}>{card.state}</span>
                </Link>
              ),
            )}
          </div>
        </div>
      ))}
    </section>
  );
}
